import React from "react";
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableContainer,
  TableFooter,
  TableHead,
  TableRow,
  Tooltip,
} from "@mui/material";
import PerformanceBar from "../PerformanceBar/PerformanceBar";
import {
  BodyCell,
  HeaderCell,
  CellContent,
  CellContentVariant,
  NestedBodyCell,
  NestedHeaderCell,
} from "./TeamPerformanceTable.styles";
import Initials from "../Initials/Initials";
// @ts-ignore
import InfoIcon from "../../icon/infoIcon.svg?react";
import { TeamMetrics } from "../../api/generated";
import getInitials from "../../util/getInitials";
import LegendKey from "../PerformanceTableLegendKey/PerformanceTableLegendKey";

export interface Props {
  userMetrics: Array<TeamMetrics>;
  displayPerformanceBar?: boolean;
}

export default function TeamPerformanceTable(props: Props) {
  function renderRow(row: TeamMetrics, isTotal = false) {
    const userCompleted =
      row.counts && row.counts.length > 0
        ? row.counts.reduce(
            (subAcc, countItem) => subAcc + countItem.completed_items,
            0
          )
        : 0;
    const userAssigned =
      row.counts && row.counts.length > 0
        ? row.counts.reduce(
            (subAcc, countItem) => subAcc + countItem.assigned_items,
            0
          )
        : 0;
    const userUnassigned =
      row.counts && row.counts.length > 0
        ? row.counts.reduce(
            (subAcc, countItem) => subAcc + countItem.unassigned_items,
            0
          )
        : 0;

    const colors = [
      "#57599E",
      "#A296CC",
      "#7B6AB8",
      "#454899",
      "#3A238C"
    ];

    const randomColor = colors[Math.floor(Math.random() * colors.length)];

    return (
		<TableRow key={row.user_id} sx={{backgroundColor: isTotal? "var(--secondary-hover)" : "inherit", borderBottom: '1px solid #E5E5E5', '&:last-child': {border: 'none'}}}>
        <BodyCell
          scope="row"
          sx={{
            verticalAlign: isTotal ? "inherit": "top",
            width: "1%",
          }}
        >
          <Initials rounded={!isTotal} randomColor={isTotal? "#191C66" : randomColor}>
            {isTotal ? "T" : getInitials(row.user_name)}
          </Initials>
        </BodyCell>
        <BodyCell
          scope="row"
          sx={{
            textAlign: "left",
            verticalAlign: isTotal ? "center" : "top",
            fontWeight: 500,
            ...(isTotal && { fontWeight: 700 }),
          }}
        >
          {row.name}
        </BodyCell>
        <BodyCell>
          <Table sx={{ minWidth: 650, }} aria-label="performance table">
            <TableBody>
              {row.counts &&
                row.counts.map((count, key) => (
                  <TableRow key={key}>
                    <NestedBodyCell
                      scope="row"
                      sx={{
                        textAlign: "left",
                        width: "40%",
                      }}
                    >
                      <CellContent key={key} sx={{textTransform: 'capitalize'}}>{count.opco}</CellContent>
                    </NestedBodyCell>
                    <NestedBodyCell
                      scope="row"
                      sx={{
                        ...(isTotal && { fontWeight: 500 }),
                      }}
                    >
                      {isTotal ? (
                        <CellContent
                          sx={{fontWeight: '700'}}
                        >
                          {userCompleted}
                        </CellContent>
                      ) : (
                        <CellContent
                          key={key}
                          variant={
                            count.completed_items > 0
                              ? CellContentVariant.GREY
                              : CellContentVariant.LIGHTGREY
                          }
                        >
                          {count.completed_items}
                        </CellContent>
                      )}
                    </NestedBodyCell>
                    <NestedBodyCell
                      scope="row"
                      sx={{
                        ...(isTotal && { fontWeight: '700' }),
                      }}
                    >
                      {isTotal ? (
                        <CellContent
                          sx={{fontWeight: '700'}}
                        >
                          {userAssigned}
                        </CellContent>
                      ) : (
                        <CellContent
                          key={key}
                        >
                          {count.assigned_items}
                        </CellContent>
                      )}
                    </NestedBodyCell>
                    <NestedBodyCell
                      scope="row"
                      sx={{
                        ...(isTotal && { fontWeight: 700 }),
                      }}
                    >
                      {isTotal ? (
                        <CellContent
                        sx={{fontWeight: '700'}}
                        >
                          {userUnassigned}
                        </CellContent>
                      ) : (
                        <CellContent
                          key={key}
                        >
                          {count.unassigned_items}
                        </CellContent>
                      )}
                    </NestedBodyCell>
                    {props.displayPerformanceBar && (
                      <NestedBodyCell
                        scope="row"
                        sx={{
                          width: "25%",
                          textAlign: "left",
                        }}
                      >
                        <PerformanceBar
                          key={key}
                          completed={count.completed_items}
                          assigned={count.assigned_items}
                          unassigned={count.unassigned_items}
                          multiplier={5}
                          max={50}
                        />
                      </NestedBodyCell>
                    )}
                  </TableRow>
                ))}
            </TableBody>
          </Table>
        </BodyCell>
      </TableRow>
    );
  }

  // Calculate team totals
  const teamTotal = {
    user_id: -1,
    user_email: "",
    name: "Team total",
    team_id: -1,
    user_name: "",
    counts: [
      {
        opco: "",
        completed_items: props.userMetrics.reduce((acc, item) => {
          return acc + (item.counts ? item.counts.reduce((subAcc, countItem) => subAcc + countItem.completed_items, 0) : 0);
        }, 0),
        assigned_items: props.userMetrics.reduce((acc, item) => {
          return acc + (item.counts ? item.counts.reduce((subAcc, countItem) => subAcc + countItem.assigned_items, 0) : 0);
        }, 0),
        unassigned_items: props.userMetrics.reduce((acc, item) => {
          return acc + (item.counts ? item.counts.reduce((subAcc, countItem) => subAcc + countItem.unassigned_items, 0) : 0);
        }, 0),
      },
    ],
  };

  return (
    <TableContainer component={Paper} sx={{ boxShadow: 'none', borderRadius: '6px' }}>
      <Table sx={{ minWidth: 650 }} aria-label="simple table">
        <TableHead>
          <TableRow>
            <HeaderCell sx={{ width: "1%" }}></HeaderCell>
            <HeaderCell sx={{ width: "24%" }}></HeaderCell>
            <HeaderCell>
              <Table sx={{ minWidth: 650 }} aria-label="simple table">
                <TableBody>
                  <TableRow>
                    <NestedHeaderCell
                      scope="row"
                      sx={{
                        textAlign: "left",
                        width: "40%",
                      }}
                    >
                      <CellContent>Business Unit</CellContent>
                    </NestedHeaderCell>
                    <NestedHeaderCell scope="row">
                      <CellContent>Completed</CellContent>
                    </NestedHeaderCell>
                    <NestedHeaderCell scope="row">
                      <CellContent>Assigned</CellContent>
                    </NestedHeaderCell>
                    <NestedHeaderCell scope="row">
                      <CellContent>
                        <Tooltip
                          placement="top"
                          title="Invoices the person unassigned from themselves"
                          style={{
                            display: "flex",
                            gap: "4px",
                            justifyContent: "flex-end",
                          }}
                        >
                          <span>
                            Unassigned{" "}
                            <InfoIcon
                              style={{ alignSelf: "center", minWidth: "16px" }}
                            />
                          </span>
                        </Tooltip>
                      </CellContent>
                    </NestedHeaderCell>
                    {props.displayPerformanceBar && (
                      <NestedHeaderCell sx={{ width: "25%" }}></NestedHeaderCell>
                    )}
                  </TableRow>
                </TableBody>
              </Table>
            </HeaderCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {renderRow(teamTotal, true)}
          {props.userMetrics.map((row) => renderRow(row, false))}
        </TableBody>
        {props.displayPerformanceBar && (
          <TableFooter>
            <TableRow>
              <HeaderCell colSpan={7} align="right" sx={{ textAlign: "right" }}>
                <Box
                  sx={{
                    display: "flex",
                    gap: "10px",
                    justifyContent: "flex-end",
                  }}
                >
                  <LegendKey label="Completed" color="var(--green-blue)" />
                  <LegendKey label="Assigned" color="var(--mint)" />
                  <LegendKey label="Unassigned" color="var(--amber)" />
                  </Box>
              </HeaderCell>
            </TableRow>
          </TableFooter>
        )}
      </Table>
    </TableContainer>
  );
}